// src/service/planCountryImageService.jsx

import { axiosSubscriptionInstance, axiosAuthInstance } from '../configs/AxiosInstance';

/**
 * Fetch all plan country images
 * @param {Function} get - Get function from ApiContext
 * @returns {Promise<Object>} { success: boolean, data: any, message: string }
 */
export const fetchPlanCountryImages = async (get) => {
  try {
    const response = await get('/get-plan-country-images', { 
      instance: axiosSubscriptionInstance 
    });
    return {
      success: true,
      data: response.data || [],
    };
  } catch (error) {
    console.error("Error fetching plan country images:", error);
    return {
      success: false,
      message: error?.response?.data?.detail || "Failed to fetch plan country images. Please try again.",
    };
  }
};

/**
 * Fetch all plans
 * @param {Function} get - Get function from ApiContext
 * @param {String} event_type_id - Event type ID
 * @returns {Promise<Object>} { success: boolean, data: any, message: string }
 */
export const fetchAllPlans = async (get, event_type_id) => {
  try {
    const query = `/fetch-all-plans?event_type_id=${event_type_id}`;
    const response = await get(query, { instance: axiosSubscriptionInstance });
    return {
      success: true,
      data: response.data?.plans || [],
    };
  } catch (error) {
    console.error("Error fetching plans:", error);
    return {
      success: false,
      message: error?.response?.data?.detail || "Failed to fetch plans. Please try again.",
    };
  }
};

/**
 * Upload image file
 * @param {Function} post - Post function from ApiContext
 * @param {File} file - Image file to upload
 * @returns {Promise<Object>} { success: boolean, data: any, message: string }
 */
export const uploadImage = async (post, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await post('/upload?category=banner', formData, {
      instance: axiosAuthInstance,
    });
    
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("Error uploading image:", error);
    return {
      success: false,
      message: error?.response?.data?.detail || "Failed to upload image. Please try again.",
    };
  }
};

/**
 * Modify plan images (save image URL and name)
 * @param {Function} post - Post function from ApiContext
 * @param {String} event_type_id - Event type ID
 * @param {Object} payload - Payload with plan_images array
 * @returns {Promise<Object>} { success: boolean, data: any, message: string }
 */
export const modifyPlanImages = async (post, event_type_id, payload) => {
  try {
    const requestPayload = {
      event_type_id,
      plan_images: payload.plan_images || [],
    };
    
    const { data } = await post('/modify-plan-images', requestPayload, {
      instance: axiosSubscriptionInstance,
    });
    
    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error("Error modifying plan images:", error);
    return {
      success: false,
      message: error?.response?.data?.detail || "Failed to save image. Please try again.",
    };
  }
};

/**
 * Modify plan country images (link image_id with plan_id)
 * @param {Function} post - Post function from ApiContext
 * @param {String} event_type_id - Event type ID
 * @param {Object} payload - Payload with plan_country_images array
 * @returns {Promise<Object>} { success: boolean, data: any, message: string }
 */
export const modifyPlanCountryImages = async (post, event_type_id, payload) => {
  try {
    const requestPayload = {
      event_type_id,
      plan_country_images: payload.plan_country_images || [],
    };
    
    const { data } = await post('/modify-plan-country-images', requestPayload, {
      instance: axiosSubscriptionInstance,
    });
    
    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error("Error modifying plan country images:", error);
    return {
      success: false,
      message: error?.response?.data?.detail || "Failed to link image with plan. Please try again.",
    };
  }
};

/**
 * Delete plan country image association
 * @param {Function} post - Post function from ApiContext
 * @param {String} event_type_id - Event type ID
 * @param {String} plan_country_image_id - Plan country image ID to delete
 * @returns {Promise<Object>} { success: boolean, data: any, message: string }
 */
export const deletePlanCountryImageAssociation = async (post, event_type_id, plan_country_image_id) => {
  try {
    const requestPayload = {
      event_type_id,
      plan_country_images: [
        {
          operation: "DELETE",
          id: plan_country_image_id,
        },
      ],
    };
    
    const { data } = await post('/modify-plan-country-images', requestPayload, {
      instance: axiosSubscriptionInstance,
    });
    
    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error("Error deleting plan country image association:", error);
    return {
      success: false,
      message: error?.response?.data?.detail || "Failed to delete image association. Please try again.",
    };
  }
};

/**
 * Delete entire plan image
 * @param {Function} post - Post function from ApiContext
 * @param {String} event_type_id - Event type ID
 * @param {String} image_id - Image ID to delete
 * @returns {Promise<Object>} { success: boolean, data: any, message: string }
 */
export const deletePlanImage = async (post, event_type_id, image_id) => {
  try {
    const requestPayload = {
      event_type_id,
      plan_images: [
        {
          operation: "DELETE",
          id: image_id,
        },
      ],
    };
    
    const { data } = await post('/modify-plan-images', requestPayload, {
      instance: axiosSubscriptionInstance,
    });
    
    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error("Error deleting plan image:", error);
    return {
      success: false,
      message: error?.response?.data?.detail || "Failed to delete image. Please try again.",
    };
  }
};

